import React, { useState, useCallback, useEffect, useContext } from 'react';
import { CoupleHeader } from './components';
import { RegistryFooter } from './components/RegistryFooter';
import type { GuestRegistryState } from '../../GuestRegistry.controller';
import '@assets/charm.css';
import { useNativeState } from '@shared/core/native';
import { NotificationToast } from '@apps/registry/common/components/NotificationToast';
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { StyledMainSection, styles } from './GuestRegistry.styles';
import { useGuestRegistryController } from '@apps/registry/guest/routes/GuestRegistry/GuestRegistry.controller';
import { Flex } from '@withjoy/joykit';
import { Shell } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import type { OnPreviewListenerUpdate } from '../../GuestRegistryPreviewListener';
import { GuestRegistryPreviewListener } from '../../GuestRegistryPreviewListener';
import { DesignLayoutType } from '@graphql/generated-base';
import { AddExternalOrderTrackingDialog } from '@apps/registry/common/components/AddExternalOrderTracking';
import { OrderTrackingContext } from '@apps/registry/guest/providers';
import { GuestShopping } from './components/GuestShopping';
import { useToggleIntercomLauncher } from '@shared/core/intercom';
import { useColorPaletteProvider } from '@apps/guest';
import { StyleApplicatorScript } from '@shared/components/StyleApplicatorScript';

interface ContentProps extends Readonly<{ eventHandle: string; registryState: GuestRegistryState; eventId: string; handleOnPreviewUpdate: OnPreviewListenerUpdate }> {}

export const GuestRegistryContent: React.FC<ContentProps> = ({ eventHandle, registryState, eventId, handleOnPreviewUpdate }) => {
  const { toast, openToast } = useGuestRegistryController();

  const { eventDate, isAdmin, pageNote, firebaseId, registryAdminPhoto, primaryTextColor, font, pages, layout, migratedPageContainerId } = registryState;

  const [registryPhoto, setRegistryPhoto] = useState(registryAdminPhoto);

  useEffect(() => {
    setRegistryPhoto(registryAdminPhoto);
  }, [registryAdminPhoto]);

  const { isNative } = useNativeState();
  const [isToast, setIsToast] = useState<boolean>(false);
  const [isShowCartSection, setIsShowCartSection] = useState<boolean>(false);
  const { pageColor } = useColorPaletteProvider();
  const customMainSectionStyles = pageColor ? { marginX: 0 } : {};

  const { t } = useTranslation('sharedRegistry');
  const { itemAddedSuccess } = t('shoppingCart');

  const showToast = useCallback(() => {
    setIsShowCartSection(true);
    setIsToast(true);
    withWindow(global => global.setTimeout(() => setIsToast(false), 3000), 0);
    withWindow(global => global.setTimeout(() => setIsShowCartSection(false), 10000), 0);
  }, []);

  const { isAddExternalOrderTrackingDialogOpen, closeAddExternalOrderTrackingDialog, orderForTrackingRequest } = useContext(OrderTrackingContext);

  if (!eventId) {
    return null;
  }

  return (
    <>
      <GuestRegistryPreviewListener
        eventHandle={eventHandle}
        setRegistryPhoto={setRegistryPhoto}
        pages={[...(pages || [])]}
        onUpdate={handleOnPreviewUpdate}
      />
      <NotificationToast {...toast} />
      {!isInIframe() && <StyleApplicatorScript />}
      <NotificationToast
        isPositionTop={false}
        notificationText={itemAddedSuccess()}
        notificationVisible={isToast}
        setNotificationVisible={setIsToast}
      />
      <Shell
        eventDesign={registryState.eventDesign}
        eventHandle={eventHandle}
        noTranslucency
        isAlohaGuestPage={layout === DesignLayoutType.aloha}
        topPadding="visible"
      >
        <CoupleHeader
          eventDate={eventDate}
          isAdmin={isAdmin}
          pageNote={pageNote}
          website={eventHandle}
          firebaseId={firebaseId}
          eventId={eventId}
          isNative={isNative}
          registryPhoto={registryPhoto}
          font={font}
          pages={pages}
          layout={layout}
          primaryTextColor={primaryTextColor}
          migratedPageContainerId={migratedPageContainerId}
        />
        <Flex __css={styles.wrapperNewMvp}>
          <StyledMainSection __css={{ ...styles.mainSection, ...customMainSectionStyles }}>
            <GuestShopping
              eventHandle={eventHandle}
              eventId={eventId}
              registryState={registryState}
              showToast={showToast}
              isShowCartSection={isShowCartSection}
            />
            <RegistryFooter />
          </StyledMainSection>
          <AddExternalOrderTrackingDialog
            openToast={openToast}
            onClose={closeAddExternalOrderTrackingDialog}
            isOpen={isAddExternalOrderTrackingDialogOpen}
            orderId={orderForTrackingRequest?.id || ''}
            orderQuantity={orderForTrackingRequest?.lineItems[0]?.quantity.toString() || '1'}
            orderTitle={orderForTrackingRequest?.lineItems[0]?.frozenProductData.title}
            orderPhotoUrl={orderForTrackingRequest?.lineItems[0]?.frozenProductData.photos[0]?.url}
            orderNumber={orderForTrackingRequest?.fulfillment?.orderNumber}
            trackingNumber={orderForTrackingRequest?.fulfillment?.shipments?.[0]?.trackingInformation?.trackingNumber}
            email={orderForTrackingRequest?.email || ''}
            isPurchased={orderForTrackingRequest?.isPurchased || false}
          />
        </Flex>
      </Shell>
    </>
  );
};

interface Props extends Readonly<{ eventHandle: string; registryState: GuestRegistryState; eventId: string; handleOnPreviewUpdate: OnPreviewListenerUpdate }> {}

const GuestRegistry: React.FC<Props> = ({ eventHandle, registryState, eventId, handleOnPreviewUpdate }) => {
  useToggleIntercomLauncher({ show: true, minScreenSize: 'mobile' });

  return (
    <GuestRegistryContent
      eventHandle={eventHandle}
      eventId={eventId}
      registryState={registryState}
      handleOnPreviewUpdate={handleOnPreviewUpdate}
    />
  );
};

export default GuestRegistry;
